// The lines of one parts list: module-scope columns for the shared DataTable.
//
// Contract 1 (shared-table.md): the columns never change identity. Everything
// that moves per render (what is saving, what failed, which row is asking to be
// removed) arrives through the table's `meta` as a LineMeta. A cell that edits
// keeps its OWN draft in local state: it is a stable component type, so the
// input survives a re-render with its caret.

import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import type { ColumnDef } from '@tanstack/react-table';
import { ChipToggle } from '../components/ui/ChipToggle';
import { Field } from '../components/ui/Field';
import { Select, type SelectOption } from '../components/ui/Select';
import Reveal from '../components/ui/Reveal';
import { Tag } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import {
  formatQty,
  lineVariantLabel,
  toVariantTag,
  variantLabel,
  type BomLine,
  type BomMember,
} from './bomsApi';

export type EditableField = 'quantity_per' | 'traced' | 'variant';

export interface LineMeta {
  /** A writer, looking at the active revision. */
  canEdit: boolean;
  /** Lines that set a build's number, from capacity[].limited_by. */
  limiterIds: Set<number>;
  /** Values on their way to the server, by `pendKey`. */
  pending: Record<string, unknown>;
  /** The last failure per line, in words. */
  errors: Record<number, string>;
  confirmingId: number | null;
  /** Family lines whose parts list is folded away. Open is the default, so the set holds the exceptions. */
  hiddenMemberIds: Set<number>;
  onToggleMembers: (lineId: number) => void;
  onPatch: (line: BomLine, field: EditableField, value: number | boolean | string | null) => void;
  onFail: (lineId: number, message: string) => void;
  onAskRemove: (lineId: number | null) => void;
  onRemove: (line: BomLine) => void;
}

export const pendKey = (lineId: number, field: EditableField) => `${lineId}:${field}`;

export const lineName = (l: BomLine): string =>
  (l.part_family_id !== null ? l.family_label : l.display_name) || l.target_name;

const metaOf = (info: { table: { options: { meta?: unknown } } }) =>
  info.table.options.meta as LineMeta;

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

// ── Per unit ─────────────────────────────────────────────────────────────────

const QtyCell: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  const saved = formatQty(line.quantity_per);
  const pending = meta.pending[pendKey(line.id, 'quantity_per')];
  const [draft, setDraft] = useState(saved);
  // Escape sets this before the blur it causes, so the blur does not save what
  // the person just walked away from.
  const cancelled = useRef(false);

  // Truth from the server (or a revert after a failure) replaces the draft.
  // Depends on `pending` so a failed save, which leaves `saved` unchanged,
  // still puts the old number back.
  useEffect(() => {
    if (pending === undefined) setDraft(saved);
  }, [pending, saved]);

  if (!meta.canEdit) return <span>{saved}</span>;

  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const n = Number(draft);
    if (draft.trim() === '' || !Number.isInteger(n) || n <= 0) {
      setDraft(saved);
      meta.onFail(line.id, 'Per unit takes whole numbers above zero.');
      return;
    }
    if (n === Number(line.quantity_per)) {
      setDraft(saved);
      return;
    }
    meta.onPatch(line, 'quantity_per', n);
  };

  return (
    <Field
      size="sm"
      type="number"
      inputMode="numeric"
      step="1"
      min="1"
      value={draft}
      aria-label={`Per unit, ${lineName(line)}`}
      aria-busy={pending !== undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          cancelled.current = true;
          setDraft(saved);
          e.currentTarget.blur();
        }
      }}
      className={`w-20 text-right ${pending !== undefined ? 'motion-safe:animate-pulse' : ''}`}
    />
  );
};

// ── Scanned ──────────────────────────────────────────────────────────────────

const TracedCell: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  const pending = meta.pending[pendKey(line.id, 'traced')];
  const traced = pending === undefined ? line.traced : (pending as boolean);
  const label = traced ? 'Scanned at the bench' : 'Not scanned';
  if (!meta.canEdit) {
    return <span className={traced ? 'text-on-surface' : 'text-on-surface-variant'}>{label}</span>;
  }
  return (
    <ChipToggle
      size="sm"
      checked={traced}
      onChange={() => meta.onPatch(line, 'traced', !traced)}
    >
      {label}
      <span className="sr-only"> for {lineName(line)}</span>
    </ChipToggle>
  );
};

// ── Variant ──────────────────────────────────────────────────────────────────

const OTHER = '__other__';
const KNOWN = ['standard', 'pyro'];

/** All builds, Standard, Pyro, or a typed tag. Shared by the row and the add form. */
export const VariantControl: React.FC<{
  value: string | null;
  onCommit: (value: string | null) => void;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}> = ({ value, onCommit, ariaLabel, disabled, className = '' }) => {
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const done = useRef(false);

  if (typing) {
    const finish = (save: boolean) => {
      if (done.current) return;
      done.current = true;
      const tag = toVariantTag(draft);
      setTyping(false);
      if (save && tag) onCommit(tag);
    };
    return (
      <Field
        size="sm"
        autoFocus
        value={draft}
        placeholder="Build tag, such as pyro"
        aria-label={`${ariaLabel}, type a tag`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            finish(true);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            finish(false);
          }
        }}
        className={`w-40 ${className}`}
      />
    );
  }

  const unknown = value !== null && !KNOWN.includes(value);
  const options: SelectOption[] = [
    { key: '', label: 'All builds' },
    { key: 'standard', label: 'Standard' },
    { key: 'pyro', label: 'Pyro' },
    ...(unknown ? [{ key: value as string, label: variantLabel(value) }] : []),
  ];
  return (
    <Select
      size="sm"
      width="fit"
      label={ariaLabel}
      options={options}
      value={value ?? ''}
      disabled={disabled}
      onChange={(key) => onCommit(key === '' ? null : key)}
      actions={[
        {
          key: OTHER,
          label: 'Other…',
          onSelect: () => {
            done.current = false;
            setDraft('');
            setTyping(true);
          },
        },
      ]}
      className={className}
    />
  );
};

const VariantCell: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  const pending = meta.pending[pendKey(line.id, 'variant')];
  const value = pending === undefined ? line.variant : (pending as string | null);
  if (!meta.canEdit) {
    return (
      <span className={value === null ? 'text-on-surface-variant' : 'text-on-surface'}>
        {lineVariantLabel(value)}
      </span>
    );
  }
  return (
    <VariantControl
      value={value}
      ariaLabel={`Variant, ${lineName(line)}`}
      onCommit={(v) => {
        if (v !== line.variant) meta.onPatch(line, 'variant', v);
      }}
    />
  );
};

// ── Part ─────────────────────────────────────────────────────────────────────

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Tag variant="code" size="sm" className="shrink-0">
    {children}
  </Tag>
);

const MemberRow: React.FC<{ m: BomMember }> = ({ m }) => {
  const name = m.base_name || m.display_name || m.sku;
  return (
    <li className={`grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 text-xs ${m.counted ? '' : 'text-on-surface-variant'}`}>
      <Tooltip label={m.display_name || name} truncated>
        <span className="truncate">
          <span className={m.counted ? 'text-on-surface' : ''}>{name}</span>
          {m.variant_detail && <span className="text-on-surface-variant"> ({m.variant_detail})</span>}
        </span>
      </Tooltip>
      <span className="flex items-baseline gap-2 whitespace-nowrap">
        {!m.counted && <span>not counted: {m.not_counted_reason ?? 'retired'}</span>}
        <span className={`tabular-nums ${m.counted ? 'text-on-surface' : ''}`}>{m.stock}</span>
      </span>
    </li>
  );
};

const FamilyMembers: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  const members = line.members ?? [];
  if (members.length === 0) {
    return <div className="mt-1 text-xs text-on-surface-variant">No parts in this family yet</div>;
  }
  const hidden = meta.hiddenMemberIds.has(line.id);
  return (
    <div className="mt-1">
      <button
        type="button"
        aria-expanded={!hidden}
        onClick={() => meta.onToggleMembers(line.id)}
        className={`rounded px-1 -mx-1 text-xs text-on-surface-variant hover:text-primary transition-colors ${FOCUS}`}
      >
        {hidden ? `Show parts (${members.length})` : 'Hide parts'}
      </button>
      <Reveal show={!hidden}>
        <div className="mt-1 min-w-0 border-l-2 border-outline-variant pl-2.5">
          <p className="mb-1 truncate text-[11px] text-on-surface-variant">
            Any of these, at least {formatQty(line.quantity_per)} per unit
          </p>
          <ul className="space-y-0.5">
            {members.map((m) => (
              <MemberRow key={m.part_catalog_id} m={m} />
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  );
};

const PartCell: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  const isFamily = line.part_family_id !== null;
  const error = meta.errors[line.id];
  return (
    <div className="min-w-0">
      <div className="flex items-baseline gap-1.5 min-w-0">
        <span className="truncate font-medium text-on-surface">{lineName(line)}</span>
        {isFamily && <Chip>Any revision</Chip>}
        {!isFamily && line.item_is_active === false && <Chip>retired</Chip>}
      </div>
      {!isFamily && line.sku && (
        <div className="truncate font-mono text-[11px] text-on-surface-variant">{line.sku}</div>
      )}
      {isFamily && <FamilyMembers line={line} meta={meta} />}
      <Alert show={!!error} variant="text" className="mt-0.5 !text-xs">
        Not saved: {error}
      </Alert>
    </div>
  );
};

// ── Builds ───────────────────────────────────────────────────────────────────

const BuildsCell: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  const limiting = meta.limiterIds.has(line.id);
  if (!limiting) return <span>{line.builds}</span>;
  // The ring is the emphasis; the word is the second signal, so color is
  // never the only thing that says which line runs out first.
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[10px] text-primary">runs out first</span>
      <span className="rounded-md px-2 py-0.5 font-semibold text-on-surface ring-2 ring-primary">
        {line.builds}
      </span>
    </span>
  );
};

// ── Remove ───────────────────────────────────────────────────────────────────

const RemoveCell: React.FC<{ line: BomLine; meta: LineMeta }> = ({ line, meta }) => {
  if (meta.confirmingId === line.id) {
    return (
      <div
        className="flex items-center justify-end gap-1.5"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            meta.onAskRemove(null);
          }
        }}
      >
        <span className="text-xs text-on-surface-variant">Take it off the list?</span>
        <button
          type="button"
          autoFocus
          onClick={() => meta.onRemove(line)}
          className={buttonClass('danger', 'sm')}
        >
          Remove
        </button>
        <button
          type="button"
          onClick={() => meta.onAskRemove(null)}
          className={buttonClass('quiet', 'sm')}
        >
          Keep
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => meta.onAskRemove(line.id)}
      aria-label={`Remove ${lineName(line)}`}
      className={buttonClass('danger-quiet', 'sm')}
    >
      Remove
    </button>
  );
};

// ── Columns ──────────────────────────────────────────────────────────────────

export const LINE_COLUMNS: ColumnDef<BomLine, any>[] = [
  {
    id: 'part',
    header: 'Part',
    meta: { cellClass: 'max-w-[26rem]' },
    cell: (info) => <PartCell line={info.row.original} meta={metaOf(info)} />,
  },
  {
    id: 'per_unit',
    header: 'Per unit',
    meta: { align: 'right', numeric: true },
    cell: (info) => <QtyCell line={info.row.original} meta={metaOf(info)} />,
  },
  {
    id: 'scanned',
    header: 'Scanned',
    cell: (info) => <TracedCell line={info.row.original} meta={metaOf(info)} />,
  },
  {
    id: 'variant',
    header: 'Variant',
    cell: (info) => <VariantCell line={info.row.original} meta={metaOf(info)} />,
  },
  {
    id: 'stock',
    header: 'On the shelf',
    meta: { align: 'right', numeric: true },
    cell: (info) => info.row.original.stock,
  },
  {
    id: 'builds',
    header: 'Builds',
    meta: { align: 'right', numeric: true },
    cell: (info) => <BuildsCell line={info.row.original} meta={metaOf(info)} />,
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { align: 'right' },
    cell: (info) => <RemoveCell line={info.row.original} meta={metaOf(info)} />,
  },
];
